import { useEffect, useRef } from 'react';
import { FormikProps, setNestedObjectValues } from 'formik';

/**
 * Mark every field touched and run Formik validate so InputField/SelectField
 * (which only render errorMessage when touched) show errors under the inputs.
 */
export function revealFormikValidationErrors<Values>(formik: FormikProps<Values>): void {
    formik.setTouched(setNestedObjectValues(formik.values as object, true) as FormikProps<Values>['touched'], true);
}

/**
 * When the claim editor has evaluated validation (edit-load or submit), show
 * field-level errors. Pass primitive signal/evaluated from an observer parent
 * so MobX actually re-renders the form.
 *
 * Flush pending persist only once per signal. Persist can `enableReinitialize`
 * and wipe `touched`; the follow-up pass (new `initialValues`) re-reveals.
 */
export function useClaimEditorRevealFieldErrors<Values>(
    formik: FormikProps<Values>,
    validationSignal: number,
    validationEvaluated: boolean,
    onBeforeReveal?: () => void,
): void {
    const lastFlushedSignal = useRef(-1);

    useEffect(() => {
        if (!validationEvaluated && validationSignal <= 0) {
            return;
        }
        if (lastFlushedSignal.current !== validationSignal) {
            lastFlushedSignal.current = validationSignal;
            onBeforeReveal?.();
        }
        revealFormikValidationErrors(formik);
        // formik is a new object every render; initialValues re-runs after enableReinitialize.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validationSignal, validationEvaluated, formik.initialValues]);
}
